export const USERS_SERVICE_URLS = {
  REGISTER: 'users/register',
  LOGIN: 'users/login',
  GOOGLE_LOGIN: 'users/login/google',
  GOOGLE_LINK_CONFIRM_PASSWORD: 'users/login/google/confirm-password',
  GOOGLE_LINK_PASSWORD_RECOVERY_REQUEST: 'users/login/google/password-recovery/request',
  TELEGRAM_LOGIN: 'users/login/telegram',
  TELEGRAM_LOGIN_NONCE: 'users/login/telegram/nonce',
  TELEGRAM_LINK: 'users/current-user/auth/telegram',
  GOOGLE_LINK: 'users/current-user/auth/google',
  PASSWORD_RECOVERY_REQUEST: 'users/password-recovery/request',
  PASSWORD_RECOVERY_VALIDATE: 'users/password-recovery/validate',
  PASSWORD_RECOVERY_CONFIRM: 'users/password-recovery/confirm',
  CURRENT_USER: 'users/current-user',
  DELIVERY_INFORMATION: 'users/delivery-information',
};

export interface LoginData {
  email: string;
  password: string;
}

export interface GoogleLoginData {
  idToken: string;
}

export interface GoogleAccountLinkPasswordData extends GoogleLoginData {
  password: string;
}

export interface TelegramLoginData {
  idToken?: string;
  nonce?: string;
  widgetAuth?: TelegramWidgetAuthData;
}

export interface TelegramWidgetAuthData {
  id: number;
  first_name?: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  auth_date: number;
  hash: string;
}

export interface TelegramLoginNonceData {
  nonce: string;
}

export interface RegisterData {
  email: string;
  password: string;
  name: string;
  lastName: string;
  phoneNumber: string;
  nickname?: string;
}

export interface PasswordRecoveryRequestData {
  email: string;
}

export interface PasswordRecoveryRequestResultData {
  message: string;
}

export interface PasswordRecoveryTokenData {
  token: string;
}

export interface PasswordRecoveryTokenValidationData {
  isValid: boolean;
  maskedEmail?: string | null;
  expiresAt?: string | null;
  pendingAuthProvider?: AuthConnectionType | null;
}

export interface PasswordRecoveryConfirmData {
  token: string;
  newPassword: string;
}

export interface LoginResponse {
  token: string;
}

export type AuthConnectionType = 'Email' | 'Phone' | 'Google' | 'Telegram';

export interface GoogleLoginResultData {
  requiresAccountLink: boolean;
  auth?: LoginResponse | null;
  email?: string | null;
  maskedEmail?: string | null;
}

export interface PasswordRecoveryConfirmResultData {
  auth?: LoginResponse | null;
  linkedAuthProvider?: AuthConnectionType | null;
}

export interface AuthConnectionData {
  type: AuthConnectionType;
  isLinked: boolean;
}

export enum DeliveryProvider {
  NovaPost = 'NovaPost',
  UkrPost = 'UkrPost',
  Self = 'Self',
}

export enum DeliveryDestination {
  Department = 'Department',
  ParcelLocker = 'ParcelLocker',
  Address = 'Address',
}

export interface DeliveryInformationData {
  id?: number;
  label?: string | null;
  name: string;
  lastName: string;
  phone: string;
  provider: DeliveryProvider;
  destination: DeliveryDestination;
  city: string;
  number: string;
  displayName?: string;
  countryCode?: string;
  regionName?: string | null;
  districtName?: string | null;
  isPrimary: boolean;
  novaPoshtaAddress?: NovaPoshtaDeliveryAddressData | null;
  ukrPoshtaAddress?: UkrPoshtaDeliveryAddressData | null;
}

export type DeliveryInformationPayload = Omit<DeliveryInformationData, 'id'>;

export interface NovaPoshtaDeliveryAddressData {
  cityRef: string;
  settlementRef?: string | null;
  cityName: string;
  areaName?: string | null;
  regionName?: string | null;
  settlementType?: string | null;
  warehouseRef?: string | null;
  warehouseNumber?: string | null;
  warehouseDescription?: string | null;
  warehouseShortAddress?: string | null;
  warehouseTypeRef?: string | null;
  warehouseCategory?: string | null;
  postalCode?: string | null;
  streetRef?: string | null;
  streetName?: string | null;
  streetType?: string | null;
  buildingNumber?: string | null;
  flat?: string | null;
  rawProviderJson?: string | null;
}

export interface UkrPoshtaDeliveryAddressData {
  regionId?: string | null;
  districtId?: string | null;
  cityId?: string | null;
  cityKoatuu?: string | null;
  cityKatottg?: string | null;
  postcode?: string | null;
  postOfficeId?: string | null;
  postOfficeName?: string | null;
  streetId?: string | null;
  streetName?: string | null;
  houseNumber?: string | null;
  apartmentNumber?: string | null;
  rawProviderJson?: string | null;
}

export interface UserInfoData {
  name: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  nickname?: string | null;
  authConnections: AuthConnectionData[];
  deliveryInformation: DeliveryInformationData[];
}
